import { useEffect, useMemo, useState } from 'react';
import orgApi from '../../api/organizations';
import ConfirmModal from '../UI/ConfirmModal';
import Drawer from '../UI/Drawer';
import {
  primaryBtn, primaryBtnStyle, ghostBtn, ghostBtnStyle, inputCls, inputStyle, fieldLabel, fieldLabelStyle,
  monoLabel, errMessage,
} from '../UI/ink';

// Permissions grouped by the pages they unlock. A permission may appear under
// more than one group (assets:read gates several pages); both checkboxes then
// reflect the same value.
const PERMISSION_GROUPS = [
  { name: 'My Assets · Import Assets', acts: [['View', 'assets:read'], ['Add', 'assets:create'], ['Edit', 'assets:update'], ['Delete', 'assets:delete']] },
  { name: 'Network Map · Agent Registry · Baseline · Vulnerabilities', acts: [['View', 'assets:read']] },
  { name: 'Security Alerts · Cases · Findings', acts: [['View', 'events:read'], ['Create', 'events:create'], ['Edit and close', 'events:update'], ['Delete', 'events:delete']] },
  { name: 'Response Actions', acts: [['View', 'actions:read'], ['Create', 'actions:create'], ['Confirm', 'actions:confirm']] },
  { name: 'Playbooks · ChatOps', acts: [['View', 'playbooks:view'], ['Approve', 'playbooks:approve'], ['View ChatOps', 'chatops:view'], ['Execute ChatOps', 'chatops:execute']] },
  { name: 'Reports', acts: [['View', 'reports:read'], ['Create', 'reports:create'], ['Edit', 'reports:update'], ['Delete', 'reports:delete']] },
  { name: 'Members & Roles', acts: [['View', 'users:read'], ['Invite', 'users:create'], ['Edit', 'users:update'], ['Remove', 'users:delete'], ['Reset passwords', 'users:password_reset'], ['View roles', 'roles:read'], ['Create roles', 'roles:create'], ['Edit roles', 'roles:update'], ['Delete roles', 'roles:delete'], ['Assign roles', 'roles:assign']] },
  { name: 'Settings', acts: [['View', 'settings:read'], ['Edit', 'settings:update'], ['Integrations', 'settings:integrations'], ['API keys', 'settings:api_keys']] },
  { name: 'Organisation & audit', acts: [['Edit organisation', 'system:update'], ['Create organisations', 'orgs:create'], ['Audit log', 'logs:read']] },
];

// Every role that inherits (directly or transitively) from `roleId`.
const descendantsOf = (roleId, roles) => {
  const out = new Set();
  let grew = true;
  while (grew) {
    grew = false;
    roles.forEach(r => {
      if (!out.has(r.id) && (r.parent_role_id === roleId || out.has(r.parent_role_id))) {
        out.add(r.id);
        grew = true;
      }
    });
  }
  return out;
};

const RoleDrawer = ({ open, role, roles, permissions, memberCount, canSave, canDelete, tenantId, onClose, onSaved }) => {
  const isNew = !role;
  const isSystem = !!role?.is_system;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(role?.name || '');
    setDescription(role?.description || '');
    setParentId(role?.parent_role_id || '');
    setSelected(new Set(role?.direct_permissions || []));
    setError(null);
    setConfirmDelete(false);
  }, [open, role]);

  const known = useMemo(
    () => new Set((permissions || []).map(p => p.name || `${p.resource}:${p.action}`)),
    [permissions],
  );

  // Hide checkboxes for permissions this deployment doesn't define.
  const groups = useMemo(() => PERMISSION_GROUPS
    .map(g => ({ ...g, acts: known.size ? g.acts.filter(([, perm]) => known.has(perm)) : g.acts }))
    .filter(g => g.acts.length), [known]);

  const parent = roles.find(r => r.id === parentId) || null;
  const inherited = useMemo(() => new Set(parent?.effective_permissions || []), [parent]);
  const parentOptions = useMemo(() => {
    if (!role) return roles;
    const blocked = descendantsOf(role.id, roles);
    return roles.filter(r => r.id !== role.id && !blocked.has(r.id));
  }, [role, roles]);

  const locked = isSystem || !canSave;
  const toggle = (perm) => {
    if (locked || inherited.has(perm)) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(perm)) next.delete(perm); else next.add(perm);
      return next;
    });
  };

  const added = [...selected].filter(p => !inherited.has(p));
  const total = new Set([...inherited, ...selected]).size;
  const summary = parent
    ? `${total} permissions · ${inherited.size} from ${parent.name}, ${added.length} added`
    : `${total} permission${total === 1 ? '' : 's'}`;

  const save = async () => {
    if (!name.trim()) { setError('Role name is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      // System roles can't be renamed or re-parented, and their permissions are fixed.
      const payload = isSystem
        ? { description }
        : { name: name.trim(), description, permissions: [...selected], parent_role_id: parentId || null };
      if (isNew) await orgApi.createRole(tenantId, payload);
      else await orgApi.updateRole(tenantId, role.id, payload);
      await onSaved();
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Failed to save role.'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setConfirmDelete(false);
    setSaving(true);
    try {
      await orgApi.deleteRole(tenantId, role.id);
      await onSaved();
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Failed to delete role.'));
    } finally {
      setSaving(false);
    }
  };

  const title = isNew ? 'Create role' : `Edit role · ${role.name}`;
  const subtitle = isSystem
    ? 'System role. Its name, inheritance and permissions are fixed; you can edit the description.'
    : 'Changes apply to everyone with this role within 60 seconds.';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker="ROLE"
      title={title}
      subtitle={subtitle}
      footer={(
        <>
          <span className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[13px] font-medium">{summary}</span>
            {!isNew && (
              <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>
                {memberCount} member{memberCount === 1 ? ' has' : 's have'} this role
              </span>
            )}
          </span>
          <div className="flex gap-2.5 shrink-0">
            {!isNew && !isSystem && canDelete && (
              <button type="button" onClick={() => setConfirmDelete(true)} disabled={saving} className={ghostBtn} style={{ ...ghostBtnStyle, color: 'var(--sev-crit-fg)' }}>
                Delete role
              </button>
            )}
            {canSave ? (
              <button type="button" onClick={save} disabled={saving} className={primaryBtn} style={primaryBtnStyle}>
                {saving ? 'Saving…' : isNew ? 'Create role' : 'Save role'}
              </button>
            ) : (
              <button type="button" onClick={onClose} className={ghostBtn} style={ghostBtnStyle}>Close</button>
            )}
          </div>
        </>
      )}
    >
      <div className="flex flex-col gap-[18px]">
        {error && (
          <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>
            {error}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="role-name" className={fieldLabel} style={fieldLabelStyle}>Role name</label>
            <input id="role-name" type="text" value={name} onChange={e => setName(e.target.value)} disabled={locked} placeholder="e.g. SOC Lead" className={inputCls} style={inputStyle} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="role-parent" className={fieldLabel} style={fieldLabelStyle}>Inherits from</label>
            <select id="role-parent" value={parentId} onChange={e => setParentId(e.target.value)} disabled={locked} className={inputCls} style={inputStyle}>
              <option value="">No parent</option>
              {parentOptions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="role-desc" className={fieldLabel} style={fieldLabelStyle}>Description</label>
          <input id="role-desc" type="text" value={description} onChange={e => setDescription(e.target.value)} disabled={!canSave} placeholder="What people with this role do" className={inputCls} style={inputStyle} />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>PERMISSIONS BY PAGE</span>
          <span className="flex items-center gap-3.5 text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>
            {parent && (
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 box-border rounded-[3px] border" style={{ background: 'var(--surf-raised)', borderColor: 'var(--surf-line-strong)' }} />
                From {parent.name}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-[3px]" style={{ background: 'var(--accent)' }} />
              Added here
            </span>
          </span>
        </div>

        <div className="flex flex-col rounded-[10px] border overflow-hidden" style={{ borderColor: 'var(--surf-line)' }}>
          {groups.map(g => {
            const visible = g.acts.some(([, perm]) => inherited.has(perm) || selected.has(perm));
            return (
              <div key={g.name} className="flex flex-col gap-2 px-3.5 py-[11px] border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)', background: 'var(--surf-bg)' }}>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13.5px] font-semibold">{g.name}</span>
                  <span className="font-mono text-[10.5px] shrink-0" style={{ color: visible ? 'var(--ink-muted)' : 'var(--ink-faint)' }}>
                    {visible ? 'VISIBLE' : 'HIDDEN'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                  {g.acts.map(([label, perm]) => {
                    const fromParent = inherited.has(perm);
                    const disabled = fromParent || locked;
                    return (
                      <label
                        key={perm}
                        title={fromParent ? `Inherited from ${parent.name}` : perm}
                        className="flex items-center gap-[7px] min-h-[26px] text-[12.5px]"
                        style={{ color: fromParent ? 'var(--ink-muted)' : 'var(--ink-text)', cursor: disabled ? 'default' : 'pointer' }}
                      >
                        <input
                          type="checkbox"
                          checked={fromParent || selected.has(perm)}
                          disabled={disabled}
                          onChange={() => toggle(perm)}
                          className="m-0 w-[15px] h-[15px]"
                          style={{ accentColor: fromParent ? 'var(--ink-faint)' : 'var(--accent)' }}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmDelete}
        title="Delete role"
        message={memberCount > 0
          ? `${memberCount} member${memberCount === 1 ? ' is' : 's are'} assigned ${role?.name}. They will lose these permissions.`
          : `Delete the ${role?.name} role?`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </Drawer>
  );
};

export default RoleDrawer;
